import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useCallable, CallStatus } from '../lib/useCallable';
import CandidateFormSettings from './CandidateFormSettings.jsx';
import CandidateFormBuilder from './CandidateFormBuilder.jsx';
import { useCollection, useDoc } from '../lib/useFirestore';
import CandidateBanner from '../components/CandidateBanner';
import Field from '../components/Field';
import { evaluateCondition } from '../lib/conditions';
import { usePageIdentity } from '../lib/usePageIdentity';

/**
 * Candidate conflict-disclosure forms: issuing a link, seeing who has replied, and reading one.
 *
 * The list deliberately shows only who was asked and whether they have answered. A candidate's
 * form contains their five-year client history, their family's directorships and any malpractice
 * claims against them; that is fetched one form at a time, by somebody who chose to open it,
 * rather than loaded into a page anyone might leave on a screen.
 */

const input =
  'rounded border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900';

const STATUS_LABEL = {
  invited: 'Invited',
  in_progress: 'Started',
  submitted: 'Submitted',
};
const STATUS_TONE = {
  invited: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  in_progress: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  submitted: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
};

const when = (value) => {
  if (!value) return '—';
  const date = value.seconds ? new Date(value.seconds * 1000) : new Date(value._seconds ? value._seconds * 1000 : value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export default function CandidateForms() {
  // Its own tab, matching the candidate's. /ccc is a separate tool, so a tab reading "New Matter
  // Intake" would be as wrong here as it is on the candidate's side.
  usePageIdentity({ title: 'Candidate Conflict Check — AALRR', icon: '/ccc-favicon.svg' });

  // Settings are firm-wide, so only an admin sees the editor. Everybody else sees the warning
  // that one is missing, worded as something to ask about rather than something to do.
  const { isAdmin, isFormEditor } = useAuth();
  const { run, busy, error } = useCallable();
  const [params, setParams] = useSearchParams();
  const [forms, setForms] = useState(null);
  const [scope, setScope] = useState('mine');
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const blankDraft = {
    candidateEmail: '',
    candidateName: '',
    position: '',
    office: '',
    practiceGroupId: '',
    practiceGroupLeader: '',
  };
  const [draft, setDraft] = useState(blankDraft);

  // Where completed forms are sent. Shown here rather than buried, because a form nobody
  // receives is the failure this page is most likely to produce.
  const { data: settings } = useDoc('settings/candidateForm');
  // The firm's own practice groups and people, so neither the office nor the practice group
  // leader has to be typed from memory into a form a candidate will read.
  const practiceGroups = useCollection('practiceGroups');
  const roster = useCollection('roster');
  const hrSet = Boolean(
    settings && (Array.isArray(settings.hrRecipients) ? settings.hrRecipients.length : String(settings.hrRecipients || '').trim())
  );
  // The domain the candidate will see. Worth saying out loud here, because this link is often a
  // candidate's first interaction with the firm after an offer and nobody wants to discover the
  // hostname after sending twenty of them.
  const publicBase = String((settings && settings.publicBaseUrl) || '').trim();
  const onDefaultDomain = !publicBase;

  /**
   * The questions. Fetched for everybody, not only admins: the builder needs them to edit, and
   * the invite form needs the office list out of them.
   *
   * Reading the office list from the schema rather than repeating it here is the point — it is
   * the same list the candidate's own Office dropdown is built from, so the two cannot drift into
   * offering different offices.
   */
  const [schema, setSchema] = useState(null);
  const refresh = useCallback(async () => {
    const result = await run('listCandidateForms', {});
    if (result) {
      setForms(result.forms || []);
      setScope(result.scope || 'mine');
    }
  }, [run]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (schema) return;
    let cancelled = false;
    (async () => {
      const result = await run('getCandidateFormSchema', {});
      if (!cancelled && result) setSchema(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [schema, run]);

  const issue = async (sendEmail) => {
    setCopied(false);
    const result = await run('createCandidateFormLink', { ...draft, sendEmail });
    if (!result) return;
    setIssued(result);
    setDraft(blankDraft);
    refresh();
  };

  /** The offices the candidate's own dropdown offers, taken from that very field. */
  const offices = useMemo(() => {
    const field = (schema?.sections || [])
      .flatMap((section) => section.fields || [])
      .find((f) => f.path === 'candidate.office');
    return (field?.options || []).map((option) => option.value);
  }, [schema]);

  /**
   * Each practice group with its leader's name resolved through the roster.
   *
   * The `practice_group_leader` ROLE is on one person in the whole roster, so it is not the
   * source — the group document's own `practiceGroupLeaderUid` is, which is what the intake
   * form's autofill uses too. A group whose leader is unset or no longer on the roster still
   * appears; it simply has no name to offer, which is more useful than hiding the group.
   */
  const groups = useMemo(() => {
    const byUid = new Map((roster.data || []).map((person) => [person.id, person]));
    return (practiceGroups.data || [])
      .filter((group) => group.isActive !== false)
      .map((group) => ({
        id: group.id,
        name: group.name || group.id,
        leader: (byUid.get(group.practiceGroupLeaderUid) || {}).displayName || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [practiceGroups.data, roster.data]);

  // Office included: the candidate's form shows it locked, so a link issued without one used to
  // ask the candidate for something they had no way to give.
  const incomplete =
    !draft.candidateEmail.trim() || !draft.candidateName.trim() || !draft.position.trim() || !draft.office;

  /**
   * Killing a link.
   *
   * Confirmed, because it cannot be undone in the sense that matters: the candidate's current
   * link stops working immediately and the only way back is a new one. The answers are untouched.
   */
  const revoke = async (form) => {
    const reason = window.prompt(
      `Switch off the link for ${form.candidateName}?

Their current link stops working immediately. Anything they have already filled in is kept.

Why? (optional, recorded in the audit log)`,
      ''
    );
    // Cancel returns null; an empty string is somebody choosing not to give a reason.
    if (reason === null) return;
    const result = await run('revokeCandidateFormLink', { formId: form.formId, reason });
    if (result) refresh();
  };

  /**
   * Deleting a form. Admin only on the server, and confirmed by typing the candidate's name.
   *
   * A click-through confirm is too easy on an action that destroys a record of what a named
   * third party declared about their professional history. Typing the name means reading the row
   * you are about to delete.
   */
  const remove = async (form) => {
    const typed = window.prompt(
      `Delete ${form.candidateName}'s form?

This destroys their answers and the stored PDF. ${
        form.status === 'submitted'
          ? 'It HAS been submitted — the copy emailed to HR is unaffected, but this app will no longer hold the record.'
          : 'It was never submitted.'
      }

Type the candidate's name to confirm:`,
      ''
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== form.candidateName.trim().toLowerCase()) {
      window.alert('That name did not match, so nothing was deleted.');
      return;
    }
    const result = await run('deleteCandidateForm', { formId: form.formId });
    if (result) {
      if (openFormId === form.formId) setParams({});
      refresh();
    }
  };

  const reissue = async (form, sendEmail) => {
    const result = await run('reissueCandidateFormLink', { formId: form.formId, sendEmail });
    if (!result) return;
    setIssued(result);
    setCopied(false);
    refresh();
  };

  const openFormId = params.get('form');

  return (
    /*
      Its own frame, because this page is outside the app Shell — see App.jsx. It is a separate
      tool that shares a deployment, not a step in opening a matter, so it carries no matter
      navigation and supplies the padding and background Shell would otherwise have given it.
    */
    <main className="min-h-full bg-slate-100 pb-8 dark:bg-slate-950 sm:pb-12">
      {/* The version pill is here and not on the candidate's page: this is the firm's side, and
          knowing which build you are looking at is how a "that isn't what I see" conversation
          gets settled. */}
      <CandidateBanner title="Candidate conflict check" showVersion showSignOut />
      <div className="mx-auto max-w-4xl space-y-5 px-4 pt-8 sm:px-6 sm:pt-12">
        {/* One heading. The frame added when this page left the Shell duplicated the one already
            here, so it said "Candidate conflict check" and "Candidate forms" one above the other. */}
        <header className="border-b border-slate-300 pb-5 dark:border-slate-700">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Create a private link to the conflict check and professional disclosure form for a
            candidate. Send it yourself, or have the app email it for you. They fill the form in
            without signing in — the link is what identifies them — and the completed form is
            emailed to HR as a PDF when they submit it.
          </p>
        </header>

      {!hrSet && (
        <p className="rounded border border-red-400 bg-red-50 p-3 text-sm text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
          <strong>No HR recipient is set, so a completed form has nowhere to go.</strong>{' '}
          {isAdmin
            ? 'Set one under Settings below before inviting anyone.'
            : 'Ask an administrator to set one before inviting anyone.'}{' '}
          Links still work and answers are still saved, but nothing will be emailed.
        </p>
      )}

      {/* The fix for the warning above, in the same place as the warning. The domain note that
          used to sit here is a hint on the field it is about, rather than a third banner. */}
      {isAdmin && <CandidateFormSettings settings={settings} onDefaultDomain={onDefaultDomain} />}
      {isFormEditor && schema ? <CandidateFormBuilder settings={settings} sections={schema.sections} /> : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Invite a candidate</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-600 dark:text-slate-300">
            Candidate email
            <input
              className={`${input} mt-1 w-full`}
              type="email"
              placeholder="name@example.com"
              value={draft.candidateEmail}
              onChange={(e) => setDraft({ ...draft, candidateEmail: e.target.value })}
            />
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-300">
            Candidate name
            <input
              className={`${input} mt-1 w-full`}
              value={draft.candidateName}
              onChange={(e) => setDraft({ ...draft, candidateName: e.target.value })}
            />
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-300">
            Position
            <input
              className={`${input} mt-1 w-full`}
              placeholder="Associate Attorney"
              value={draft.position}
              onChange={(e) => setDraft({ ...draft, position: e.target.value })}
            />
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-300">
            Office
            {/* The firm's own offices, from the same list the candidate's own dropdown is built
                from, so the two cannot come to offer different offices. */}
            <select
              className={`${input} mt-1 w-full`}
              value={draft.office}
              onChange={(e) => setDraft({ ...draft, office: e.target.value })}
            >
              <option value="">— choose —</option>
              {offices.map((office) => (
                <option key={office} value={office}>
                  {office}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
            Practice group leader
            {/*
              The leaders themselves, because that is the field — the candidate's form asks for a
              Practice Group Leader, so this offers the people who are one rather than making
              somebody pick a group and infer.

              Each is labelled with the group they lead, since two leaders' names mean less than
              "Brian M. Wheeler — Commercial & Complex Lit." to whoever is sending the invitation.

              Sourced from each group's own `practiceGroupLeaderUid`, resolved through the roster:
              the same record Admin → Roster & roles writes, so changing a leader there changes
              this. NOT the `practice_group_leader` role, which sounds like the right source and
              is on exactly one person in a roster of 494.
            */}
            <select
              className={`${input} mt-1 w-full`}
              value={draft.practiceGroupId}
              onChange={(e) => {
                const group = groups.find((g) => g.id === e.target.value);
                setDraft({
                  ...draft,
                  practiceGroupId: e.target.value,
                  practiceGroupLeader: group ? group.leader : '',
                });
              }}
            >
              <option value="">— choose, or leave blank —</option>
              {groups
                .filter((group) => group.leader)
                .map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.leader} — {group.name}
                  </option>
                ))}
            </select>
            <span className="mt-1 block text-slate-500 dark:text-slate-400">
              {draft.practiceGroupLeader
                ? `The candidate’s form will show ${draft.practiceGroupLeader}.`
                : 'Optional. From the firm’s own record of who leads each practice group.'}
            </span>
          </label>
        </div>
        {/* Pre-filled and locked on the candidate's form: these are the firm's answers, not theirs. */}
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Name, position, office and practice group leader appear on the candidate&rsquo;s form
          already filled in and not editable by them.
        </p>
        {/*
          Two ways out, because both are real. A partner writing to a candidate they have been
          talking to for weeks wants the link in their own email, from their own address, in the
          thread they are already in — an automated message from the intake system is a worse
          first impression and arrives somewhere else. Recruiting sending twelve of them wants the
          app to do the sending.
        */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => issue(false)}
            disabled={busy || incomplete}
            className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
          >
            Generate a link for me to send
          </button>
          <button
            type="button"
            onClick={() => issue(true)}
            disabled={busy || incomplete}
            className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:border-firm-500 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200"
          >
            Generate and email it to the candidate
          </button>
          <CallStatus busy={busy} error={error} saved={false} />
        </div>

        {issued && (
          <div className="mt-4 rounded border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100">
            <p className="font-medium">
              {issued.emailed ? 'Invitation emailed to the candidate.' : 'Link created. Nothing has been emailed.'}
            </p>
            {/* Shown either way: a link the firm has no copy of is one it cannot chase. */}
            <p className="mt-1">
              {issued.emailed ? 'Here is the same link, if you need to send it another way:' : 'Send this to the candidate:'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="break-all rounded bg-white/70 px-2 py-1 dark:bg-slate-900/60">{issued.url}</code>
              <button
                type="button"
                className="rounded border border-emerald-400 px-2 py-1 hover:bg-white/60 dark:border-emerald-600"
                onClick={() => {
                  navigator.clipboard?.writeText(issued.url);
                  setCopied(true);
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-2">
              Treat it like a password: anyone holding it can complete the form as this candidate.
            </p>
            {issued.warning && <p className="mt-2 font-medium">{issued.warning}</p>}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {scope === 'all' ? 'All forms' : 'Forms you have sent'}
            {forms ? <span className="ml-2 font-normal text-slate-500 dark:text-slate-400">{forms.length}</span> : null}
          </h3>
          <button
            type="button"
            onClick={refresh}
            className="text-xs text-firm-700 underline dark:text-firm-300"
          >
            Refresh
          </button>
        </div>
        {/*
          Said plainly, because "why can I not see so-and-so's candidate?" is otherwise a support
          question, and because a link that cannot be shown again is a property worth knowing
          rather than a limitation to discover.
        */}
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {scope === 'all'
            ? 'As an administrator you see every form, so you can switch off a link on anyone’s behalf.'
            : 'You see the forms you sent. An administrator can see them all.'}{' '}
          A link itself can never be shown again — only its fingerprint is stored, which is what
          makes a database leak useless. Use <strong>New link</strong> to issue a working one,
          which switches the previous off.
        </p>

        {forms === null ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : forms.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">No candidate forms yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[860px] text-xs">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400">
                  <th className="py-1 pr-3">Candidate</th>
                  <th className="py-1 pr-3">Position</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">Invited</th>
                  <th className="py-1 pr-3">Submitted</th>
                  <th className="w-px py-1" />
                </tr>
              </thead>
              <tbody>
                {forms.map((form) => (
                  <tr key={form.formId} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1.5 pr-3">
                      <div className="font-medium text-slate-800 dark:text-slate-100">{form.candidateName}</div>
                      <div className="text-slate-500 dark:text-slate-400">{form.candidateEmail}</div>
                    </td>
                    <td className="py-1.5 pr-3 text-slate-700 dark:text-slate-200">
                      {form.position}
                      {form.office ? ` — ${form.office}` : ''}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className={`rounded px-1.5 py-0.5 ${STATUS_TONE[form.status] || STATUS_TONE.invited}`}>
                        {STATUS_LABEL[form.status] || form.status}
                      </span>
                      {/* A form whose link is dead but whose answers are intact looks identical
                          to a live one otherwise, so it says so. */}
                      {form.linkLive === false ? (
                        <span
                          className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-red-900 dark:bg-red-950 dark:text-red-100"
                          title={
                            form.linkRevokedReason
                              ? `Revoked${form.linkRevokedByName ? ` by ${form.linkRevokedByName}` : ''}: ${form.linkRevokedReason}`
                              : `Revoked${form.linkRevokedByName ? ` by ${form.linkRevokedByName}` : ''}`
                          }
                        >
                          link off
                        </span>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-600 dark:text-slate-300">{when(form.invitedAt)}</td>
                    <td className="py-1.5 pr-3 text-slate-600 dark:text-slate-300">{when(form.submittedAt)}</td>
                    <td className="w-px py-1.5 text-right align-top">
                      {/* nowrap, not wrap: with four actions the column was breaking "Delete"
                          onto its own line, which reads as a separate row's control. The cell is
                          given the width it needs instead — the table already scrolls
                          horizontally, so the right trade is a wider table, not a stacked one. */}
                      <div className="flex flex-nowrap items-center justify-end gap-3 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-firm-700 underline dark:text-firm-300"
                          onClick={() => setParams({ form: form.formId })}
                        >
                          Open
                        </button>
                        {/* No new link once a form is submitted: it would not let anybody edit a
                            submitted disclosure, so offering one would be a lie. */}
                        {form.status !== 'submitted' ? (
                          <button
                            type="button"
                            className="text-firm-700 underline dark:text-firm-300"
                            onClick={() => reissue(form, true)}
                            disabled={busy}
                          >
                            New link
                          </button>
                        ) : null}
                        {form.linkLive === false ? null : (
                          <button
                            type="button"
                            className="text-red-600 underline dark:text-red-400"
                            onClick={() => revoke(form)}
                            disabled={busy}
                          >
                            Switch off
                          </button>
                        )}
                        {isAdmin ? (
                          <button
                            type="button"
                            className="text-red-700 underline dark:text-red-400"
                            onClick={() => remove(form)}
                            disabled={busy}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

        {openFormId && <CandidateFormDetail formId={openFormId} onClose={() => setParams({})} />}
      </div>
    </main>
  );
}

/** One candidate's answers, fetched only when somebody chooses to open it. */
function CandidateFormDetail({ formId, onClose }) {
  const { run, busy, error } = useCallable();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await run('getCandidateForm', { formId });
      if (!cancelled) setData(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [run, formId]);

  if (busy && !data) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading the form…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  }
  if (!data) return null;

  const { form, sections, pdfUrl } = data;
  const answers = form.answers || {};

  return (
    <section className="rounded-lg border border-firm-500 bg-white p-4 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {form.candidateName} — {form.position}
        </h3>
        <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_TONE[form.status] || STATUS_TONE.invited}`}>
          {STATUS_LABEL[form.status] || form.status}
        </span>
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded bg-firm-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
          >
            Open the PDF
          </a>
        )}
        {/* The searchable version of the same answers. Offered next to the PDF because the two
            are used by different people for different things: the PDF is the record of what was
            declared, the workbook is what somebody runs the searches from. */}
        {data.workbookUrl && (
          <a
            href={data.workbookUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-firm-700 px-3 py-1.5 text-xs font-medium text-firm-700 hover:bg-firm-50 dark:border-firm-400 dark:text-firm-200 dark:hover:bg-slate-800"
          >
            Download the conflicts spreadsheet
          </a>
        )}
        <button type="button" onClick={onClose} className="ml-auto text-xs text-slate-500 underline dark:text-slate-400">
          Close
        </button>
      </div>

      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Invited by {form.invitedByName || 'unknown'} on {when(form.invitedAt)}
        {form.submittedAt ? `. Submitted ${when(form.submittedAt)} from ${form.candidateEmail}.` : '. Not yet submitted.'}
      </p>

      {/* Read-only, using the same controls the candidate saw, so what is on screen here is what
          they were looking at rather than a second rendering that could differ. */}
      <div className="mt-4 space-y-5">
        {(sections || []).map((section) => {
          const fields = (section.fields || []).filter(
            (field) => field.visibleIf === undefined || evaluateCondition(field.visibleIf, answers)
          );
          if (!fields.length) return null;
          return (
            <div key={section.id}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {section.title}
              </h4>
              <div className="mt-2 space-y-3">
                {fields.map((field) => (
                  <div key={field.path}>
                    <Field field={field} matter={answers} config={{}} onChange={() => {}} disabled />
                    {(form.bypasses || {})[field.path] && (
                      <p className="mt-1 rounded border-l-2 border-slate-400 bg-slate-50 px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        Left blank because: {form.bypasses[field.path]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
